// One putt in the shared game page. Port of the app's PuttCard (grade block,
// distance + break, pace bar, entry / miss line). Click opens the ball path
// with the same stat chips the app shows in its detail modal.
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCrosshairs, faXmark, faRotateRight } from '@fortawesome/free-solid-svg-icons';

import { red } from '../../constants/Colors';
import { computePuttDisplay } from './puttDisplay';
import PuttPath from './PuttPath';
import PuttReplay from './PuttReplay';

function StatsPanel({ d, putt, index }) {
  const { made, paceText, angDiff, grade, gradeColor, accentColor, breakLabel, resultText, resultColor, speedBarColor } = d;

  return (
    <div style={{ marginTop: 16 }}>
      <div className="hrowBetween">
        <div className="hrow">
          <div className="puttNum" style={{ width: 22, height: 22, fontSize: 10, backgroundColor: accentColor, marginRight: 10 }}>{index + 1}</div>
          <div className="bold" style={{ fontSize: 16 }}>{putt.distance}ft · {breakLabel}</div>
        </div>
        <div className="bold" style={{ fontSize: 28, color: gradeColor }}>{grade}</div>
      </div>

      <div className="hrow" style={{ gap: 8, marginTop: 12 }}>
        <div className="chip">
          <div className="hrow" style={{ marginBottom: 6 }}>
            <FontAwesomeIcon icon={faBolt} color={speedBarColor} style={{ fontSize: 11 }} />
            <span className="chipLabel">Pace</span>
          </div>
          <div className="bold" style={{ fontSize: 15, color: speedBarColor }}>{paceText}</div>
        </div>
        <div className="chip">
          <div className="hrow" style={{ marginBottom: 6 }}>
            <FontAwesomeIcon icon={faCrosshairs} color={resultColor} style={{ fontSize: 11 }} />
            <span className="chipLabel">Entry</span>
          </div>
          <div className="bold" style={{ fontSize: 14, color: resultColor }}>{resultText}</div>
          {made && (
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
              {Math.abs(angDiff) < 5 ? 'Dead center' : `${Math.abs(angDiff).toFixed(1)}° ${angDiff > 0 ? 'right' : 'left'} of center`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Ball path + stats. Plays the animated replay on mount, settles into the
// static view, and the corner button re-runs it (same as the app's PuttResultDetail).
function PuttDetail({ d, putt, index }) {
  const [replayDone, setReplayDone] = useState(false);
  const [replayKey, setReplayKey] = useState(0);

  return (
    <div>
      <div style={{ position: 'relative' }}>
        {replayDone ? (
          <PuttPath d={d} width={432} />
        ) : (
          <PuttReplay key={replayKey} d={d} width={432} onDone={() => setReplayDone(true)} />
        )}
        {replayDone && (
          <button className="replayBtn" onClick={() => { setReplayDone(false); setReplayKey(k => k + 1); }} aria-label="Replay">
            <FontAwesomeIcon icon={faRotateRight} />
          </button>
        )}
      </div>
      <StatsPanel d={d} putt={putt} index={index} />
    </div>
  );
}

export default function PuttCard({ putt, index }) {
  const [open, setOpen] = useState(false);
  const d = computePuttDisplay(putt);
  const { made, paceText, angDiff, grade, roundedScore, gradeColor, accentColor, breakLabel, resultText, resultColor, speedBarColor, speedBarPct } = d;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div>
      {open && (
        <div className="modalBackdrop" onClick={() => setOpen(false)}>
          <div className="modalCard" onClick={(e) => e.stopPropagation()}>
            <button className="modalClose" onClick={() => setOpen(false)} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <PuttDetail d={d} putt={putt} index={index} />
          </div>
        </div>
      )}

      <div className="puttCard" onClick={() => setOpen(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(true); }}>
        <div className="puttAccent" style={{ backgroundColor: accentColor }} />

        <div className="puttGrade">
          <div className="bold" style={{ fontSize: 24, color: gradeColor }}>{grade}</div>
          <div style={{ fontSize: 10, color: gradeColor, opacity: 0.6, marginTop: 1 }}>{roundedScore}</div>
        </div>

        <div className="puttMain">
          <div className="hrow">
            <div className="puttNum" style={{ backgroundColor: accentColor }}>{index + 1}</div>
            <span className="bold" style={{ fontSize: 13 }}>{putt.distance}ft</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginLeft: 6 }}>{breakLabel}</span>
          </div>

          <div style={{ marginTop: 8 }}>
            <div className="hrowBetween" style={{ marginBottom: 4 }}>
              <span className="medium" style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>Pace</span>
              <span className="bold" style={{ fontSize: 10, color: speedBarColor }}>{paceText}</span>
            </div>
            <div className="paceTrack">
              <div className="paceFill" style={{ width: `${speedBarPct * 100}%`, backgroundColor: speedBarColor }} />
            </div>
          </div>

          <div className="hrow" style={{ marginTop: 6 }}>
            {made ? (
              <span className="resultDot" style={{ backgroundColor: resultColor }} />
            ) : (
              <FontAwesomeIcon icon={faXmark} color={red} style={{ fontSize: 10, marginRight: 5 }} />
            )}
            <span className="medium" style={{ fontSize: 11, color: resultColor }}>
              {resultText}{made ? ` · ${Math.abs(angDiff).toFixed(1)}°` : ''}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
